// ─────────────────────────────────────────────────────────────────────────────
// Settings.js
// Contractor settings page — company details, logo, quote defaults, account.
//
// SECTIONS:
//   1. Subscription Summary — trial/plan status, days remaining, link to /payments
//   2. Company Logo — upload/remove
//   3. Company Information — name, email, phone, address, VAT, website
//   4. Quote Defaults — payment terms, terms & conditions
//   5. Account — email address
//
// TO ADD A NEW SECTION: Add a new card below the existing ones following
// the same cardStyle pattern.
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useEffect, useRef } from 'react'
import { supabase } from '../supabaseClient'
import Sidebar from '../components/Sidebar'
import { useNavigate } from 'react-router-dom'
import TrialBanner from '../components/TrialBanner'

export default function Settings({ session }) {
  const navigate = useNavigate()

  // ── State ──────────────────────────────────────────────────────────────────
  const [company, setCompany] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [uploadingLogo, setUploadingLogo] = useState(false)
  const [error, setError] = useState(null)
  const [message, setMessage] = useState(null)
  const [logoUrl, setLogoUrl] = useState(null)
  const fileInputRef = useRef(null)

  const [form, setForm] = useState({
    company_name: '',
    email: '',
    phone: '',
    address: '',
    vat_number: '',
    website: '',
    payment_terms: 'Payment due within 30 days',
    terms_conditions: '',
  })

  // ── Data fetching ──────────────────────────────────────────────────────────
  useEffect(() => { fetchCompany() }, [])

  const fetchCompany = async () => {
    const { data: profile } = await supabase
      .from('profiles')
      .select('company_id')
      .eq('id', session.user.id)
      .single()

    if (profile?.company_id) {
      const { data } = await supabase
        .from('companies')
        .select('*')
        .eq('id', profile.company_id)
        .single()

      if (data) {
        setCompany(data)
        setLogoUrl(data.logo_url || null)
        setForm({
          company_name: data.company_name || '',
          email: data.email || '',
          phone: data.phone || '',
          address: data.address || '',
          vat_number: data.vat_number || '',
          website: data.website || '',
          payment_terms: data.payment_terms || 'Payment due within 30 days',
          terms_conditions: data.terms_conditions || '',
        })
      }
    }
    setLoading(false)
  }

  // ── Subscription helpers ───────────────────────────────────────────────────
  // Calculates trial status and days remaining for the subscription summary card
  const getTrialInfo = () => {
    if (!company) return null
    const now = new Date()
    const trialEnd = company.trial_ends_at ? new Date(company.trial_ends_at) : null
    const daysLeft = trialEnd ? Math.ceil((trialEnd - now) / (1000 * 60 * 60 * 24)) : null

    if (company.plan_status === 'active') {
      return { label: 'Active', color: '#15803D', bg: '#F0FDF4', border: '#BBF7D0', message: 'Your subscription is active.', daysLeft: null }
    }
    if (company.plan_status === 'cancelled') {
      return { label: 'Cancelled', color: '#64748B', bg: '#F1F5F9', border: '#E2E8F0', message: 'Your subscription has been cancelled.', daysLeft: null }
    }
    if (trialEnd && daysLeft > 0) {
      return {
        label: 'Free Trial',
        color: '#2563EB', bg: '#EFF6FF', border: '#BFDBFE',
        message: `Your free trial ends on ${trialEnd.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}.`,
        daysLeft,
        urgent: daysLeft <= 3,
      }
    }
    // Trial expired
    return {
      label: 'Trial Expired',
      color: '#BE123C', bg: '#FFF1F2', border: '#FECDD3',
      message: 'Your free trial has expired. Upgrade to continue using QuoteFlow.',
      daysLeft: 0,
      urgent: true,
    }
  }

  // ── Logo handlers ──────────────────────────────────────────────────────────
  const handleLogoUpload = async (e) => {
    const file = e.target.files[0]
    if (!file) return

    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file.')
      return
    }
    if (file.size > 2 * 1024 * 1024) {
      setError('Image must be smaller than 2MB.')
      return
    }

    setUploadingLogo(true)
    setError(null)

    const fileExt = file.name.split('.').pop()
    const fileName = `${company.id}/logo.${fileExt}`

    const { error: uploadError } = await supabase.storage
      .from('logos')
      .upload(fileName, file, { upsert: true })

    if (uploadError) {
      setError(uploadError.message)
      setUploadingLogo(false)
      return
    }

    const { data: urlData } = supabase.storage.from('logos').getPublicUrl(fileName)
    const publicUrl = urlData.publicUrl

    await supabase.from('companies').update({ logo_url: publicUrl }).eq('id', company.id)
    setLogoUrl(publicUrl)
    setUploadingLogo(false)
    setMessage('Logo uploaded successfully!')
  }

  const handleRemoveLogo = async () => {
    if (!window.confirm('Remove your company logo?')) return
    await supabase.from('companies').update({ logo_url: null }).eq('id', company.id)
    setLogoUrl(null)
    setMessage('Logo removed.')
  }

  // ── Save company details ───────────────────────────────────────────────────
  const handleSave = async () => {
    if (!form.company_name.trim()) {
      setError('Company name is required.')
      return
    }
    setSaving(true)
    setError(null)
    setMessage(null)

    const { error } = await supabase
      .from('companies')
      .update({
        company_name: form.company_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        address: form.address.trim(),
        vat_number: form.vat_number.trim(),
        website: form.website.trim(),
        payment_terms: form.payment_terms.trim(),
        terms_conditions: form.terms_conditions.trim(),
      })
      .eq('id', company.id)

    if (error) setError(error.message)
    else setMessage('Settings saved successfully!')
    setSaving(false)
  }

  // ── Styles ─────────────────────────────────────────────────────────────────
  const inputStyle = {
    width: '100%', height: '44px', borderRadius: '10px',
    border: '1.5px solid #E2E8F0', padding: '0 14px',
    fontSize: '14px', outline: 'none', boxSizing: 'border-box',
    background: '#FAFAFA', fontFamily: 'Inter, sans-serif',
  }

  const labelStyle = {
    display: 'block', fontSize: '13px', fontWeight: '600',
    color: '#374151', marginBottom: '6px',
  }

  const cardStyle = {
    background: 'white', borderRadius: '12px',
    border: '1px solid #EAECF0', padding: '32px',
    maxWidth: '640px', marginBottom: '24px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
  }

  const sectionTitle = {
    fontSize: '15px', fontWeight: '700', color: '#0F172A',
    margin: '0 0 24px', paddingBottom: '16px',
    borderBottom: '1px solid #F1F5F9',
  }

  const trialInfo = getTrialInfo()

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={{
      display: 'flex', minHeight: '100vh',
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
      background: '#F7F8FC',
    }}>
      <Sidebar session={session} />
      

      <div style={{ marginLeft: '240px', flex: 1, padding: '36px 40px' }}>

        {/* ── Page Header ── */}
        <div style={{ marginBottom: '28px' }}>
          <h1 style={{
            fontSize: '22px', fontWeight: '800', color: '#0F172A',
            margin: '0 0 4px', letterSpacing: '-0.4px',
          }}>Settings</h1>
          <p style={{ fontSize: '13px', color: '#94A3B8', margin: 0 }}>
            Manage your company details and preferences
          </p>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', color: '#94A3B8', padding: '48px' }}>Loading...</div>
        ) : (
          <>
            {/* ── Status messages ── */}
            {error && (
              <div style={{
                background: '#FFF1F2', border: '1px solid #FECDD3',
                borderRadius: '8px', padding: '12px 16px',
                color: '#BE123C', fontSize: '13px',
                marginBottom: '20px', maxWidth: '640px',
              }}>{error}</div>
            )}
            {message && (
              <div style={{
                background: '#F0FDF4', border: '1px solid #BBF7D0',
                borderRadius: '8px', padding: '12px 16px',
                color: '#15803D', fontSize: '13px',
                marginBottom: '20px', maxWidth: '640px',
              }}>{message}</div>
            )}

            

            {/* ── 1. Subscription Summary Card ──────────────────────────────
                Shows current plan status, days remaining on trial, and a
                button to go to the full Payments page.
                Status is derived from company.plan_status and trial_ends_at.
            ─────────────────────────────────────────────────────────────── */}
            {trialInfo && (
              <div style={{
                ...cardStyle,
                background: trialInfo.bg,
                border: `1px solid ${trialInfo.border}`,
                padding: '24px 28px',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                      <span style={{
                        padding: '3px 10px', borderRadius: '999px', fontSize: '12px',
                        fontWeight: '700', background: 'white',
                        color: trialInfo.color, border: `1px solid ${trialInfo.border}`,
                      }}>{trialInfo.label}</span>
                      {trialInfo.daysLeft > 0 && (
                        <span style={{
                          fontSize: '13px', fontWeight: '700',
                          color: trialInfo.urgent ? '#BE123C' : trialInfo.color,
                        }}>
                          {trialInfo.daysLeft} day{trialInfo.daysLeft !== 1 ? 's' : ''} remaining
                        </span>
                      )}
                      {trialInfo.daysLeft === 0 && (
                        <span style={{ fontSize: '13px', fontWeight: '700', color: '#BE123C' }}>
                          Expired
                        </span>
                      )}
                    </div>
                    <p style={{ fontSize: '13px', color: trialInfo.color, margin: '0 0 4px', opacity: 0.85 }}>
                      {trialInfo.message}
                    </p>
                    {trialInfo.label === 'Free Trial' && (
                      <p style={{ fontSize: '12px', color: trialInfo.color, margin: 0, opacity: 0.65 }}>
                        After your trial, a paid subscription is required to continue.
                      </p>
                    )}
                  </div>

                  {/* Link to full Payments page */}
                  <button
                    onClick={() => navigate('/payments')}
                    style={{
                      padding: '8px 18px', borderRadius: '8px', fontSize: '13px',
                      fontWeight: '700', border: `1.5px solid ${trialInfo.border}`,
                      background: 'white', color: trialInfo.color,
                      cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
                      marginLeft: '16px',
                    }}
                  >
                    {trialInfo.label === 'Active' ? 'Manage Subscription' : 'View Plans →'}
                  </button>
                </div>
              </div>
            )}

            {/* ── 2. Company Logo ── */}
            <div style={cardStyle}>
              <h2 style={sectionTitle}>Company Logo</h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
                {/* Logo preview */}
                <div style={{
                  width: '100px', height: '100px', borderRadius: '12px',
                  border: '2px dashed #E2E8F0', background: '#F8FAFC',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  overflow: 'hidden', flexShrink: 0,
                }}>
                  {logoUrl ? (
                    <img src={logoUrl} alt="Company logo"
                      style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  ) : (
                    <p style={{ fontSize: '11px', color: '#94A3B8', margin: 0 }}>No logo</p>
                  )}
                </div>

                {/* Upload controls */}
                <div>
                  <p style={{ fontSize: '13px', color: '#64748B', margin: '0 0 12px' }}>
                    Upload your company logo. It will appear on your quotes and PDFs.
                    <br />
                    <span style={{ fontSize: '12px', color: '#94A3B8' }}>PNG, JPG or SVG. Max 2MB.</span>
                  </p>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button onClick={() => fileInputRef.current.click()} disabled={uploadingLogo} style={{
                      padding: '8px 16px', borderRadius: '8px',
                      background: 'linear-gradient(135deg, #2563EB, #1D4ED8)',
                      color: 'white', fontSize: '13px', fontWeight: '600',
                      border: 'none', cursor: 'pointer',
                    }}>
                      {uploadingLogo ? 'Uploading...' : logoUrl ? 'Change Logo' : 'Upload Logo'}
                    </button>
                    {logoUrl && (
                      <button onClick={handleRemoveLogo} style={{
                        padding: '8px 16px', borderRadius: '8px',
                        background: 'white', border: '1.5px solid #FECDD3',
                        color: '#BE123C', fontSize: '13px', fontWeight: '600', cursor: 'pointer',
                      }}>Remove</button>
                    )}
                  </div>
                  <input ref={fileInputRef} type="file" accept="image/*"
                    onChange={handleLogoUpload} style={{ display: 'none' }} />
                </div>
              </div>
            </div>

            {/* ── 3. Company Information ── */}
            <div style={cardStyle}>
              <h2 style={sectionTitle}>Company Information</h2>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>

                <div style={{ gridColumn: '1 / -1', marginBottom: '16px' }}>
                  <label style={labelStyle}>Company Name *</label>
                  <input type="text" value={form.company_name}
                    onChange={e => setForm({ ...form, company_name: e.target.value })}
                    placeholder="e.g. ABC Plumbing Ltd" style={inputStyle} />
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={labelStyle}>Email</label>
                  <input type="email" value={form.email}
                    onChange={e => setForm({ ...form, email: e.target.value })}
                    placeholder="info@company.com" style={inputStyle} />
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={labelStyle}>Phone</label>
                  <input type="text" value={form.phone}
                    onChange={e => setForm({ ...form, phone: e.target.value })}
                    placeholder="+44 7700 000000" style={inputStyle} />
                </div>

                <div style={{ gridColumn: '1 / -1', marginBottom: '16px' }}>
                  <label style={labelStyle}>Address</label>
                  <input type="text" value={form.address}
                    onChange={e => setForm({ ...form, address: e.target.value })}
                    placeholder="123 Main Street, London" style={inputStyle} />
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={labelStyle}>VAT Number</label>
                  <input type="text" value={form.vat_number}
                    onChange={e => setForm({ ...form, vat_number: e.target.value })}
                    placeholder="GB123456789" style={inputStyle} />
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={labelStyle}>Website</label>
                  <input type="text" value={form.website}
                    onChange={e => setForm({ ...form, website: e.target.value })}
                    placeholder="www.company.com" style={inputStyle} />
                </div>

              </div>
            </div>

            {/* ── 4. Quote Defaults ── */}
            <div style={cardStyle}>
              <h2 style={sectionTitle}>Quote Defaults</h2>

              <div style={{ marginBottom: '16px' }}>
                <label style={labelStyle}>Payment Terms</label>
                <input type="text" value={form.payment_terms}
                  onChange={e => setForm({ ...form, payment_terms: e.target.value })}
                  placeholder="e.g. Payment due within 30 days" style={inputStyle} />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={labelStyle}>Default Terms & Conditions</label>
                <textarea value={form.terms_conditions}
                  onChange={e => setForm({ ...form, terms_conditions: e.target.value })}
                  placeholder="Enter your standard terms and conditions that will appear on all quotes..."
                  style={{
                    width: '100%', height: '120px', borderRadius: '10px',
                    border: '1.5px solid #E2E8F0', padding: '12px 14px',
                    fontSize: '14px', outline: 'none', boxSizing: 'border-box',
                    background: '#FAFAFA', resize: 'vertical',
                    fontFamily: 'Inter, sans-serif',
                  }} />
              </div>
            </div>

            {/* ── Save button ── */}
            <div style={{ maxWidth: '640px', display: 'flex', justifyContent: 'flex-end', marginBottom: '24px' }}>
              <button onClick={handleSave} disabled={saving} style={{
                height: '44px', padding: '0 32px', borderRadius: '10px',
                background: 'linear-gradient(135deg, #2563EB, #1D4ED8)',
                color: 'white', fontSize: '14px', fontWeight: '700',
                border: 'none', cursor: saving ? 'not-allowed' : 'pointer',
                boxShadow: '0 2px 8px rgba(37,99,235,0.30)',
              }}>{saving ? 'Saving...' : 'Save Changes'}</button>
            </div>

            {/* ── 5. Account ── */}
            <div style={cardStyle}>
              <h2 style={sectionTitle}>Account</h2>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <p style={{ fontSize: '13px', fontWeight: '600', color: '#0F172A', margin: '0 0 2px' }}>
                    Email address
                  </p>
                  <p style={{ fontSize: '13px', color: '#94A3B8', margin: 0 }}>
                    {session?.user?.email}
                  </p>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}